import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import { getMediaById, getRelated } from "@/utils/media";
import { useFavorites } from "@/hooks/useFavorites";
import { useHistory } from "@/hooks/useHistory";
import { useSeo } from "@/hooks/useSeo";

export default function Detail() {
  const { id = "" } = useParams();
  const item = getMediaById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { recordVisit } = useHistory();
  const [toast, setToast] = useState("");

  const fav = item ? isFavorite(item.id) : false;

  useSeo({
    title: item
      ? `${item.title} - ${item.category} - 我的家庭教师`
      : "影视内容不存在 - 我的家庭教师",
    description: item
      ? `${item.synopsis.slice(0, 80)}…（${item.title}：${item.year}年${item.region}${item.category}，导演${item.director}）`
      : "抱歉，你访问的影视内容不存在或已下架。",
    keywords: item
      ? `${item.title},${item.title}${item.category},${item.actors.slice(0, 2).join(",")},${item.genre.join(",")}`
      : "影视内容不存在",
    path: `/detail/${id}`,
  });

  useEffect(() => {
    if (item) recordVisit(item.id);
  }, [item, recordVisit]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  if (!item) {
    return (
      <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-emotion-sad-line text-4xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
          影视内容不存在
        </h1>
        <p className="mt-2 max-w-md text-sm text-foreground-600">
          抱歉，该影视内容不存在或链接已失效，请从首页或分类页重新浏览。
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/"
            className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
          >
            <i className="ri-home-4-line text-base"></i>
            返回首页
          </Link>
          <Link
            to={`/category/${encodeURIComponent("电影")}`}
            className="flex h-10 items-center whitespace-nowrap rounded-md border border-background-300/60 px-5 text-sm text-foreground-800 transition-colors hover:text-foreground-950"
          >
            浏览影视分类
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelated(item, 6);

  const handleFavorite = () => {
    const nowFav = toggleFavorite(item.id);
    setToast(nowFav ? "收藏成功，可在“我的收藏”中查看" : "已取消收藏");
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-accent-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link
          to={`/category/${encodeURIComponent(item.category)}`}
          className="transition-colors hover:text-accent-400"
        >
          {item.category}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{item.title}</span>
      </nav>

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[340px_1fr]">
        <div className="mx-auto w-full max-w-[340px] lg:mx-0">
          <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
            <img
              src={item.cover}
              alt={`${item.title} 封面海报`}
              title={`${item.title} - ${item.category}`}
              className="aspect-[2/3] w-full object-cover object-top"
            />
          </div>
          <button
            type="button"
            onClick={handleFavorite}
            className={`mt-4 flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors ${
              fav
                ? "border border-accent-500/60 bg-accent-500/15 text-accent-400"
                : "bg-accent-500 text-background-50 hover:bg-accent-600"
            }`}
          >
            <i className={fav ? "ri-heart-fill text-base" : "ri-heart-line text-base"}></i>
            {fav ? "已收藏" : "收藏这部作品"}
          </button>
        </div>

        <div>
          <h1 className="font-heading text-3xl font-bold tracking-wide text-foreground-950 md:text-4xl">
            {item.title}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
            <span className="flex items-center gap-1 rounded-md bg-accent-500/15 px-2.5 py-1 font-semibold text-accent-400">
              <i className="ri-star-fill text-sm"></i>
              {item.rating} 分
            </span>
            <span className="rounded-md border border-background-300/60 px-2.5 py-1 text-foreground-700">
              {item.category}
            </span>
            <span className="text-foreground-600">
              热度 {item.popularity}
            </span>
          </div>

          <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 rounded-lg border border-background-200/70 bg-background-100 p-5 sm:grid-cols-2">
            <div className="flex gap-2 text-sm">
              <dt className="shrink-0 text-foreground-500">导演</dt>
              <dd className="text-foreground-900">{item.director}</dd>
            </div>
            <div className="flex gap-2 text-sm">
              <dt className="shrink-0 text-foreground-500">主演</dt>
              <dd className="text-foreground-900">{item.actors.join(" / ")}</dd>
            </div>
            <div className="flex gap-2 text-sm">
              <dt className="shrink-0 text-foreground-500">类型</dt>
              <dd className="text-foreground-900">{item.genre.join(" · ")}</dd>
            </div>
            <div className="flex gap-2 text-sm">
              <dt className="shrink-0 text-foreground-500">地区</dt>
              <dd className="text-foreground-900">{item.region}</dd>
            </div>
            <div className="flex gap-2 text-sm">
              <dt className="shrink-0 text-foreground-500">年份</dt>
              <dd className="text-foreground-900">{item.year}</dd>
            </div>
            <div className="flex gap-2 text-sm">
              <dt className="shrink-0 text-foreground-500">更新时间</dt>
              <dd className="text-foreground-900">{item.updateDate}</dd>
            </div>
          </dl>

          <section className="mt-8">
            <h2 className="font-heading text-xl font-semibold tracking-wide text-foreground-950">
              影片简介
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
              {item.synopsis}
            </p>
          </section>

          <section className="mt-8">
            <h2 className="font-heading text-xl font-semibold tracking-wide text-foreground-950">
              分类标签
            </h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {item.genre.map((g) => (
                <Link
                  key={g}
                  to={`/category/${encodeURIComponent(item.category)}?genre=${encodeURIComponent(g)}`}
                  className="whitespace-nowrap rounded-full border border-secondary-500/40 bg-secondary-500/10 px-3 py-1.5 text-xs text-secondary-200 transition-colors hover:border-accent-500/60 hover:text-accent-400"
                >
                  <i className="ri-price-tag-3-line mr-1 text-xs"></i>
                  {g}
                </Link>
              ))}
              {item.tags.map((t) => (
                <span
                  key={t}
                  className="whitespace-nowrap rounded-full border border-background-300/60 px-3 py-1.5 text-xs text-foreground-600"
                >
                  {t}
                </span>
              ))}
            </div>
          </section>
        </div>
      </div>

      <section className="mt-14">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h2 className="font-heading text-2xl font-semibold tracking-wide text-foreground-950">
              相关推荐
            </h2>
            <p className="mt-1.5 text-sm text-foreground-600">
              相似题材或同主演的作品，也许你会喜欢
            </p>
          </div>
          <Link
            to={`/category/${encodeURIComponent(item.category)}`}
            className="flex items-center gap-1 whitespace-nowrap text-sm text-accent-400 transition-colors hover:text-accent-300"
          >
            更多{item.category}
            <i className="ri-arrow-right-s-line text-lg"></i>
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {related.map((r) => (
            <MovieCard key={r.id} item={r} />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[70] -translate-x-1/2 animate-fade-up rounded-md border border-background-300/60 bg-background-100 px-5 py-3 text-sm text-foreground-950 shadow-xl">
          <i className="ri-checkbox-circle-line mr-1.5 text-accent-400"></i>
          {toast}
        </div>
      )}
    </div>
  );
}